/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Lista de facturas (desk) — unificada a TicketListRow (Square).
 * La tabla Excel de 8 columnas fue eliminada en la migración Square.
 */
import React from 'react';
import { TicketListRow } from '../../shared/ticket/TicketListRow';
import type { ExpenseRow } from '../domain/expenseTypes';
import { expenseErpCopy } from '../domain/expenseErpCopy';
import { todayIsoDate } from '../infra/expenseListSql';
import {
  expenseAmountResidual,
  expensePaymentStatusLabel,
  formatExpenseDateShort,
  formatExpenseMoney,
} from '../domain/expenseLabels';

type Props = {
  expenses: ExpenseRow[];
  onOpenDetail: (item: ExpenseRow) => void;
};

function expenseStatusTone(
  status: ExpenseRow['paymentStatus']
): 'ok' | 'bad' | 'warn' | 'muted' {
  if (status === 'paid') return 'ok';
  if (status === 'partial') return 'warn';
  return 'muted';
}

export function ExpensesTable({ expenses, onOpenDetail }: Props) {
  if (expenses.length === 0) return null;

  return (
    <>
      {expenses.map((item, index) => {
        const residual = expenseAmountResidual(item.amount, item.amountPaid);
        const title = item.supplierName ?? item.categoryName ?? item.categorySlug ?? '—';
        const isVoided = item.status === 'voided';
        const isOverdue =
          !isVoided && item.dueDate != null && item.paymentStatus !== 'paid' && item.dueDate < todayIsoDate();
        // Proto gastos: una sola línea de meta — vence si hay, si no la fecha de
        // la factura; el saldo residual cierra cuando corresponde.
        const metaParts: string[] = [];
        // Proto: con proveedor va la categoría; sin proveedor se explicita.
        if (item.supplierName) {
          if (item.categoryName) metaParts.push(item.categoryName);
        } else {
          metaParts.push('Sin proveedor');
        }
        if (item.description) metaParts.push(item.description);
        if (item.receiptRef) metaParts.push(`Ref. ${item.receiptRef}`);
        if (!isVoided && item.dueDate) {
          metaParts.push(isOverdue ? `Vencida ${formatExpenseDateShort(item.dueDate)}` : `Vence ${formatExpenseDateShort(item.dueDate)}`);
        } else {
          metaParts.push(formatExpenseDateShort(item.expenseDate));
        }
        if (residual > 0 && !isVoided) {
          metaParts.push(`Saldo ${formatExpenseMoney(residual)}`);
        }
        if (isVoided && item.voidReason) metaParts.push(`Anulada: ${item.voidReason}`);

        const group: 'grouped' | 'groupedLast' =
          index === expenses.length - 1 ? 'groupedLast' : 'grouped';

        return (
          <TicketListRow
            key={item.id}
            title={title}
            meta={metaParts.join(' · ') || null}
            value={
              residual > 0 && !isVoided
                ? formatExpenseMoney(residual)
                : formatExpenseMoney(item.amount)
            }
            valueTone={
              isVoided ? null : isOverdue ? 'bad' : residual > 0 && item.dueDate ? 'warn' : null
            }
            valueSub={
              !isVoided && item.amountPaid > 0 && residual > 0
                ? `de ${formatExpenseMoney(item.amount)}`
                : null
            }
            statusLabel={isVoided ? expenseErpCopy.statusVoided : expensePaymentStatusLabel(item.paymentStatus)}
            statusTone={isVoided ? 'muted' : expenseStatusTone(item.paymentStatus)}
            trailingPills={isOverdue ? [{ label: 'Vencida', tone: 'bad' as const }] : undefined}
            inactive={isVoided}
            onPress={() => onOpenDetail(item)}
            group={group}
            accessibilityLabel={`${title}, ${formatExpenseMoney(item.amount)}, ${isVoided ? expenseErpCopy.statusVoided : expensePaymentStatusLabel(item.paymentStatus)}`}
            testID={`gastos.row.${item.id}`}
          />
        );
      })}
    </>
  );
}
